import React, { ReactNode, CSSProperties, useEffect } from "react";
import { cn } from "@/utils/cssHelpers";
import { cva } from "class-variance-authority";
import { useState, Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { ArrowDown2Icon } from "@/icons/primary-icons-new";
import classNames from "classnames";

const dropdownVariants = cva(
  "relative w-full flex items-center justify-between cursor-default bg-gradient-bright rounded-sm px-5 py-3 font-sans-serif text-sm font-semibold text-white focus:outline-none",
  {
    variants: {},
  },
);

export type CaDropdownItem = {
  label: string | ReactNode;
  logo?: ReactNode;
  value: string;
  disabled?: boolean;
};

export type CaDropdownProps = {
  value?: CaDropdownItem["value"];
  options: CaDropdownItem[];
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  onChange?: (value: CaDropdownItem) => void;
};

function CaDropdown({ value, options, placeholder, className, style, onChange }: CaDropdownProps) {
  const [selected, setSelected] = useState<CaDropdownItem | undefined>(
    value ? options.find((option) => option.value === value) : undefined,
  );

  useEffect(() => {
    const selectedOption = options.find((option) => option.value === value);
    setSelected(selectedOption);
  }, [value]);

  return (
    <div className={classNames(className)} style={style}>
      <Listbox value={selected} onChange={setSelected}>
        <div className='relative mt-1'>
          <Listbox.Button className={cn(dropdownVariants())}>
            <span className='block truncate'>{selected?.label ?? placeholder}</span>
            <ArrowDown2Icon className='ml-2.5' style={{ width: 20 }} />
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options className='absolute shadow flex flex-col gap-2.5 mt-1 p-2.5 max-h-60 w-full overflow-auto rounded-sm bg-white text-sm focus:outline-none z-50'>
              {options.map((option, personIdx) => (
                <Listbox.Option
                  key={personIdx}
                  className={({ active }) =>
                    `relative cursor-default select-none ${
                      active ? "bg-light-neutral-4 text-purple font-semibold" : "text-black"
                    } ${option.disabled ? "opacity-50" : ""} `
                  }
                  value={option}
                >
                  {({ selected }) => (
                    <>
                      <div
                        className={`p-2.5 flex items-center gap-2 ${
                          selected ? "bg-light-neutral-4 text-purple font-semibold" : "font-normal"
                        }`}
                        onClick={() => onChange && !option.disabled && onChange(option)}
                      >
                        <span>{option.logo}</span>
                        <span>{option.label}</span>
                      </div>
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

export default CaDropdown;
